import React from 'react';
import { PlusIcon, XMarkIcon } from '@heroicons/react/24/outline';

const ChatSidebar = ({
  isOpen,
  onClose,
  chats,
  currentChatId,
  loadingChats,
  creatingNewChat,
  isCreatingChat,
  onStartCreateChat,
  newChatName,
  onNewChatNameChange,
  onCreateNewChat,
  onCancelCreateNewChat,
  onSwitchChat,
  editingChatId,
  editingChatName,
  onChangeEditingChatName,
  onStartEditingChat,
  onCommitEditingChat,
  onCancelEditingChat,
  onDeleteChat,
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-40" onClick={onClose}>
      <div className="fixed left-0 top-0 h-full w-80 bg-[#23242a] shadow-xl z-50" onClick={e => e.stopPropagation()}>
        <div className="flex flex-col h-full">
          <div className="flex items-center justify-between p-4 border-b border-gray-700">
            <h2 className="text-lg font-semibold text-white">Chat History</h2>
            <button onClick={onClose} className="text-gray-400 hover:text-white transition-colors">
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>

          <div className="p-4 border-b border-gray-700">
            <button
              onClick={onStartCreateChat}
              className="w-full bg-[#f5f5f0] text-gray-800 py-2 px-4 rounded-lg hover:bg-[#e8e8e0] transition-colors font-semibold flex items-center justify-center gap-2"
            >
              <PlusIcon className="w-4 h-4" />
              New Chat
            </button>
          </div>

          {creatingNewChat && (
            <div className="p-4 border-b border-gray-700">
              <input
                type="text"
                value={newChatName}
                onChange={e => onNewChatNameChange(e.target.value)}
                placeholder="Enter chat name..."
                className="w-full bg-[#343541] text-white rounded-lg px-3 py-2 border border-gray-700 focus:border-[#19C37D] focus:ring-1 focus:ring-[#19C37D]"
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    onCreateNewChat();
                  } else if (e.key === 'Escape') {
                    onCancelCreateNewChat();
                  }
                }}
                autoFocus
              />
              <div className="flex gap-2 mt-2">
                <button
                  onClick={onCreateNewChat}
                  disabled={!newChatName.trim() || isCreatingChat}
                  className={`px-3 py-1 rounded text-sm font-semibold transition-colors ${
                    newChatName.trim() && !isCreatingChat
                      ? 'bg-[#f5f5f0] text-gray-800 hover:bg-[#e8e8e0]'
                      : 'bg-gray-500 text-gray-300 cursor-not-allowed'
                  }`}
                >
                  {isCreatingChat ? 'Creating...' : 'Create'}
                </button>
                <button
                  onClick={onCancelCreateNewChat}
                  className="px-3 py-1 bg-gray-600 text-white rounded text-sm font-semibold hover:bg-gray-700 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          <div className="flex-1 overflow-y-auto">
            {loadingChats ? (
              <div className="flex items-center justify-center p-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#f5f5f0]" />
              </div>
            ) : chats.length === 0 ? (
              <div className="p-8 text-center text-gray-400">
                <p>No chats yet</p>
                <p className="text-sm mt-2">Start a search to create your first chat</p>
              </div>
            ) : (
              <div className="p-2">
                {chats.map(chat => (
                  <div
                    key={chat.id}
                    className={`group relative p-3 rounded-lg mb-2 cursor-pointer transition-colors ${
                      chat.id === currentChatId ? 'bg-[#f5f5f0] text-gray-800' : 'bg-[#343541] text-white hover:bg-[#2d2f3a]'
                    }`}
                    onClick={() => onSwitchChat(chat.id)}
                  >
                    <div className="flex items-center justify-between">
                      {editingChatId === chat.id ? (
                        <input
                          type="text"
                          value={editingChatName}
                          onChange={e => onChangeEditingChatName(e.target.value)}
                          className={`flex-1 bg-transparent border border-gray-500 rounded px-2 py-1 text-sm focus:border-[#19C37D] focus:ring-1 focus:ring-[#19C37D] ${chat.id === currentChatId ? 'text-gray-800' : 'text-white'}`}
                          onKeyDown={e => {
                            if (e.key === 'Enter') {
                              onCommitEditingChat();
                            } else if (e.key === 'Escape') {
                              onCancelEditingChat();
                            }
                          }}
                          autoFocus
                        />
                      ) : (
                        <div className="flex-1 truncate">
                          <span className={`font-medium ${chat.id === currentChatId ? 'text-gray-800' : 'text-white'}`}>{chat.name}</span>
                          <div className={`text-xs opacity-70 mt-1 ${chat.id === currentChatId ? 'text-gray-800' : 'text-white'}`}>{new Date(chat.updated_at).toLocaleDateString()}</div>
                        </div>
                      )}
                      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          onClick={e => {
                            e.stopPropagation();
                            onStartEditingChat(chat);
                          }}
                          className="p-1 hover:bg-gray-600 rounded transition-colors"
                          title="Rename chat"
                        >
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                            />
                          </svg>
                        </button>
                        <button
                          onClick={e => {
                            e.stopPropagation();
                            onDeleteChat(chat.id);
                          }}
                          className="p-1 hover:bg-red-600 rounded transition-colors"
                          title="Delete chat"
                        >
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                            />
                          </svg>
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export { ChatSidebar };
